import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import { SeoKeywordSnapshot, WpAppliedChange } from '@/types/api/seo';

type Props = {
  snapshots: SeoKeywordSnapshot[];
  appliedChanges?: WpAppliedChange[];
  metric: 'position' | 'clicks' | 'impressions';
  height?: number;
};

const COLORS = {
  position: '#4285F4',
  clicks: '#4285F4',
  impressions: '#A142F4',
  grid: 'hsla(0,0%,0%,0.08)',
  changeMarker: '#054AD9',
};

const changeTypeLabels: Record<string, string> = {
  title_tag: 'Title',
  meta_description: 'Meta',
  slug: 'Slug',
  content: 'Content',
};

const KeywordHistoryChart = function ({ snapshots, appliedChanges = [], metric, height = 200 }: Props): JSX.Element {
  if (snapshots.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">No data yet. Data populates daily from Google Search Console.</Typography>
      </Box>
    );
  }

  const changesByDate = useMemo(() => {
    const map = new Map<string, WpAppliedChange[]>();
    for (const c of appliedChanges) {
      const date = c.appliedAt.split('T')[0];
      const existing = map.get(date) ?? [];
      existing.push(c);
      map.set(date, existing);
    }
    return map;
  }, [appliedChanges]);

  return (
    <LineChart
      snapshots={snapshots}
      changesByDate={changesByDate}
      metric={metric}
      height={height}
    />
  );
};

/* ---- GSC-style line chart ---- */

const PADDING = { top: 20, right: 16, bottom: 32, left: 44 };

const LineChart = ({
  snapshots,
  changesByDate,
  metric,
  height,
}: {
  snapshots: SeoKeywordSnapshot[];
  changesByDate: Map<string, WpAppliedChange[]>;
  metric: 'position' | 'clicks' | 'impressions';
  height: number;
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const isPosition = metric === 'position';
  const color = COLORS[metric];

  // Extract values
  const values = useMemo(
    () => snapshots.map((s) => {
      if (metric === 'position') return s.position ?? null;
      if (metric === 'clicks') return s.clicks ?? 0;
      return s.impressions ?? 0;
    }),
    [snapshots, metric],
  );

  // Compute Y range
  const { minVal, maxVal, yTicks } = useMemo(() => {
    const defined = values.filter((v): v is number => v != null);
    if (defined.length === 0) return { minVal: 0, maxVal: 100, yTicks: [0, 25, 50, 75, 100] };

    if (isPosition) {
      // Position: inverted — best (lowest) at top
      const best = Math.min(...defined);
      const worst = Math.max(...defined);
      const padded = Math.max(worst + 5, best + 10);
      const top = Math.max(1, best - 2);
      const ticks = generateTicks(top, padded, 4);
      return { minVal: top, maxVal: padded, yTicks: ticks };
    }

    const max = Math.max(...defined);
    const padded = max === 0 ? 10 : Math.ceil(max * 1.15);
    const ticks = generateTicks(0, padded, 4);
    return { minVal: 0, maxVal: padded, yTicks: ticks };
  }, [values, isPosition]);

  const chartWidth = '100%';
  const chartH = height;
  const plotTop = PADDING.top;
  const plotBottom = chartH - PADDING.bottom;
  const plotH = plotBottom - plotTop;
  const plotLeft = PADDING.left;

  // Map data point to Y coordinate
  const getY = useCallback(
    (val: number | null) => {
      if (val == null) return null;
      if (isPosition) {
        // Inverted: lower position = higher on chart
        const ratio = (val - minVal) / (maxVal - minVal);
        return plotTop + ratio * plotH;
      }
      const ratio = (val - minVal) / (maxVal - minVal);
      return plotBottom - ratio * plotH;
    },
    [isPosition, minVal, maxVal, plotTop, plotBottom, plotH],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const plotRight = rect.width - PADDING.right;
      const x = e.clientX - rect.left;
      if (x < plotLeft || x > plotRight) { setHoverIdx(null); return; }
      const ratio = (x - plotLeft) / (plotRight - plotLeft);
      const idx = Math.round(ratio * (snapshots.length - 1));
      setHoverIdx(Math.max(0, Math.min(snapshots.length - 1, idx)));
    },
    [snapshots.length, plotLeft],
  );

  const handleMouseLeave = useCallback(() => setHoverIdx(null), []);

  // Build SVG path
  const buildPath = useCallback(
    (w: number) => {
      const plotRight = w - PADDING.right;
      const points: Array<{ x: number; y: number }> = [];
      const step = snapshots.length > 1 ? (plotRight - plotLeft) / (snapshots.length - 1) : 0;

      for (let i = 0; i < snapshots.length; i++) {
        const y = getY(values[i]);
        if (y == null) continue;
        points.push({ x: plotLeft + i * step, y });
      }

      if (points.length < 2) return { line: '', area: '' };

      // Smooth curve using cardinal spline
      let line = `M${points[0].x},${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const cpx = (prev.x + curr.x) / 2;
        line += ` C${cpx},${prev.y} ${cpx},${curr.y} ${curr.x},${curr.y}`;
      }

      // Area: close path to bottom
      const area = `${line} L${points[points.length - 1].x},${plotBottom} L${points[0].x},${plotBottom} Z`;

      return { line, area };
    },
    [snapshots.length, values, getY, plotLeft, plotBottom],
  );

  // X-axis date labels
  const dateLabels = useMemo(() => {
    if (snapshots.length <= 1) return snapshots.map((s, i) => ({ idx: i, date: s.snapshotDate }));
    const count = Math.min(5, snapshots.length);
    const step = Math.floor((snapshots.length - 1) / (count - 1));
    const labels: Array<{ idx: number; date: string }> = [];
    for (let i = 0; i < count; i++) {
      const idx = Math.min(i * step, snapshots.length - 1);
      labels.push({ idx, date: snapshots[idx].snapshotDate });
    }
    // Always include last
    if (labels[labels.length - 1].idx !== snapshots.length - 1) {
      labels.push({ idx: snapshots.length - 1, date: snapshots[snapshots.length - 1].snapshotDate });
    }
    return labels;
  }, [snapshots]);

  // Hover data
  const hoverSnap = hoverIdx != null ? snapshots[hoverIdx] : null;
  const hoverVal = hoverIdx != null ? values[hoverIdx] : null;
  const hoverChanges = hoverSnap ? changesByDate.get(hoverSnap.snapshotDate) : null;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: chartH }}>
      <svg
        ref={svgRef}
        width="100%"
        height={chartH}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'block', cursor: 'crosshair' }}
      >
        {/* Use a viewBox-less approach — rely on percentages via a inner group */}
        <SvgContent
          snapshots={snapshots}
          values={values}
          changesByDate={changesByDate}
          metric={metric}
          isPosition={isPosition}
          color={color}
          height={chartH}
          plotTop={plotTop}
          plotBottom={plotBottom}
          plotH={plotH}
          plotLeft={plotLeft}
          minVal={minVal}
          maxVal={maxVal}
          yTicks={yTicks}
          dateLabels={dateLabels}
          hoverIdx={hoverIdx}
          getY={getY}
          buildPath={buildPath}
        />
      </svg>

      {/* Hover tooltip */}
      {hoverSnap && hoverVal != null && (
        <HoverTooltip
          snap={hoverSnap}
          val={hoverVal}
          metric={metric}
          changes={hoverChanges ?? null}
          color={color}
        />
      )}

      {/* Change marker legend */}
      {changesByDate.size > 0 && (
        <Box sx={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: COLORS.changeMarker }} />
          <Typography variant="caption" color={COLORS.changeMarker} sx={{ fontSize: 10 }}>
            {Array.from(changesByDate.values()).reduce((s, c) => s + c.length, 0)} WP change{Array.from(changesByDate.values()).reduce((s, c) => s + c.length, 0) !== 1 ? 's' : ''} applied
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/* ---- SVG inner content (rendered inside svg — needs ref for width) ---- */

function SvgContent({
  snapshots, values, changesByDate, metric, isPosition, color, height,
  plotTop, plotBottom, plotH, plotLeft, minVal, maxVal, yTicks, dateLabels,
  hoverIdx, getY, buildPath,
}: {
  snapshots: SeoKeywordSnapshot[];
  values: (number | null)[];
  changesByDate: Map<string, WpAppliedChange[]>;
  metric: string;
  isPosition: boolean;
  color: string;
  height: number;
  plotTop: number;
  plotBottom: number;
  plotH: number;
  plotLeft: number;
  minVal: number;
  maxVal: number;
  yTicks: number[];
  dateLabels: Array<{ idx: number; date: string }>;
  hoverIdx: number | null;
  getY: (val: number | null) => number | null;
  buildPath: (w: number) => { line: string; area: string };
}) {
  // We need actual pixel width — use a ResizeObserver or fallback
  const [svgWidth, setSvgWidth] = useState(600);
  const rectRef = useRef<SVGRectElement | null>(null);
  const measRef = useCallback((node: SVGRectElement | null) => {
    rectRef.current = node;
  }, []);

  useEffect(() => {
    const node = rectRef.current;
    if (!node) return;
    const obs = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setSvgWidth(w);
    });
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  const plotRight = svgWidth - PADDING.right;
  const step = snapshots.length > 1 ? (plotRight - plotLeft) / (snapshots.length - 1) : 0;
  const { line, area } = buildPath(svgWidth);

  return (
    <>
      {/* Invisible rect for measuring width */}
      <rect ref={measRef} width="100%" height="1" opacity="0" />

      {/* Grid lines + Y labels */}
      {yTicks.map((tick) => {
        const y = isPosition
          ? plotTop + ((tick - minVal) / (maxVal - minVal)) * plotH
          : plotBottom - ((tick - minVal) / (maxVal - minVal)) * plotH;
        return (
          <g key={tick}>
            <line x1={plotLeft} y1={y} x2={plotRight} y2={y} stroke={COLORS.grid} strokeWidth={1} />
            <text x={plotLeft - 8} y={y + 4} textAnchor="end" fill="#71717A" fontSize={10} fontFamily="Plus Jakarta Sans, sans-serif">
              {isPosition ? `#${tick}` : formatNumber(tick)}
            </text>
          </g>
        );
      })}

      {/* X-axis date labels */}
      {dateLabels.map(({ idx, date }) => {
        const x = plotLeft + idx * step;
        return (
          <text key={idx} x={x} y={plotBottom + 16} textAnchor="middle" fill="#71717A" fontSize={10} fontFamily="Plus Jakarta Sans, sans-serif">
            {formatDate(date)}
          </text>
        );
      })}

      {/* Gradient fill */}
      <defs>
        <linearGradient id={`grad-${metric}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.2} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>

      {/* Area fill */}
      {area && (
        <path d={area} fill={`url(#grad-${metric})`} />
      )}

      {/* Line */}
      {line && (
        <path d={line} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      )}

      {/* Change markers — vertical dotted lines */}
      {snapshots.map((snap, i) => {
        const changes = changesByDate.get(snap.snapshotDate);
        if (!changes || changes.length === 0) return null;
        const x = plotLeft + i * step;
        return (
          <g key={`change-${i}`}>
            <line x1={x} y1={plotTop} x2={x} y2={plotBottom} stroke={COLORS.changeMarker} strokeWidth={1.5} strokeDasharray="4 3" opacity={0.5} />
            <circle cx={x} cy={plotTop - 2} r={4} fill={COLORS.changeMarker} />
          </g>
        );
      })}

      {/* Data points (dots) */}
      {snapshots.map((snap, i) => {
        const y = getY(values[i]);
        if (y == null) return null;
        const x = plotLeft + i * step;
        const isHovered = i === hoverIdx;
        return (
          <circle
            key={`dot-${i}`}
            cx={x}
            cy={y}
            r={isHovered ? 5 : 0}
            fill="#fff"
            stroke={color}
            strokeWidth={2.5}
            style={{ transition: 'r 150ms cubic-bezier(0.2, 0.8, 0.4, 1)' }}
          />
        );
      })}

      {/* Hover crosshair */}
      {hoverIdx != null && (() => {
        const x = plotLeft + hoverIdx * step;
        const y = getY(values[hoverIdx]);
        return (
          <>
            <line x1={x} y1={plotTop} x2={x} y2={plotBottom} stroke="hsla(0,0%,0%,0.15)" strokeWidth={1} strokeDasharray="3 2" />
            {y != null && (
              <line x1={plotLeft} y1={y} x2={plotRight} y2={y} stroke="hsla(0,0%,0%,0.08)" strokeWidth={1} strokeDasharray="3 2" />
            )}
          </>
        );
      })()}
    </>
  );
}

/* ---- Hover tooltip ---- */

function HoverTooltip({
  snap, val, metric, changes, color,
}: {
  snap: SeoKeywordSnapshot;
  val: number;
  metric: string;
  changes: WpAppliedChange[] | null;
  color: string;
}) {
  const isPosition = metric === 'position';
  return (
    <Box
      sx={{
        position: 'absolute', top: 4, right: 8,
        backgroundColor: 'rgba(0,0,0,0.85)', color: '#fff',
        px: 1.5, py: 1, borderRadius: '8px',
        fontSize: 12, pointerEvents: 'none', zIndex: 20,
        minWidth: 120,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, color: 'hsla(0,0%,100%,0.6)', display: 'block' }}>
        {snap.snapshotDate}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.25 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0, mt: '2px' }} />
        <Typography variant="caption" fontWeight={700} sx={{ fontSize: 14, color: '#fff' }}>
          {isPosition ? `#${val.toFixed(1)}` : val.toLocaleString()}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 10, color: 'hsla(0,0%,100%,0.6)' }}>
          {isPosition ? 'position' : metric}
        </Typography>
      </Box>
      {snap.clicks != null && !isPosition && (
        <Typography variant="caption" sx={{ fontSize: 10, color: 'hsla(0,0%,100%,0.5)', display: 'block', mt: 0.25 }}>
          {snap.clicks} clicks · {snap.impressions} impressions
        </Typography>
      )}
      {isPosition && snap.clicks != null && (
        <Typography variant="caption" sx={{ fontSize: 10, color: 'hsla(0,0%,100%,0.5)', display: 'block', mt: 0.25 }}>
          {snap.clicks} clicks · {snap.impressions} imp · CTR {snap.ctr != null ? (snap.ctr * 100).toFixed(1) + '%' : '—'}
        </Typography>
      )}
      {changes && changes.length > 0 && (
        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid hsla(0,0%,100%,0.15)' }}>
          <Typography variant="caption" sx={{ fontSize: 9, color: COLORS.changeMarker, fontWeight: 700 }}>
            WP CHANGES APPLIED
          </Typography>
          {changes.map((c, j) => (
            <Typography key={j} variant="caption" sx={{ fontSize: 10, color: 'hsla(0,0%,100%,0.7)', display: 'block' }}>
              {changeTypeLabels[c.changeType] ?? c.changeType}: {c.newValue.slice(0, 50)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ---- Helpers ---- */

function generateTicks(min: number, max: number, count: number): number[] {
  const range = max - min;
  if (range <= 0) return [min];
  const rawStep = range / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const nice = [1, 2, 5, 10].find((n) => n * magnitude >= rawStep) ?? 10;
  const step = nice * magnitude;
  const ticks: number[] = [];
  let tick = Math.ceil(min / step) * step;
  while (tick <= max) {
    ticks.push(tick);
    tick += step;
  }
  if (ticks.length === 0) ticks.push(min);
  return ticks;
}

function formatNumber(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return n.toString();
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default KeywordHistoryChart;
